import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { api } from "@/api/apiSlice";
import { backendBase } from "@/api/backendBase";
import type { WebhookLogListResponseDto } from "@/api/dtos";
import type { WebhookLog } from "./_models/WebhookLog";
import { toWebhookLogFromWebhookLogDto } from "./_transformers/toWebhookLogFromWebhookLogDto";
import { WebhookLogTags } from "./WebhookLogTags";

export interface GetWebhookLogListArgs {
  source?: string;
  event?: string;
  outcome?: string;
}

const toParams = (args: GetWebhookLogListArgs): Record<string, string> => {
  const params: Record<string, string> = {};
  if (args.source) params.source = args.source;
  if (args.event) params.event = args.event;
  if (args.outcome) params.outcome = args.outcome;
  return params;
};

/** Logged Cin7 / Shopify webhook deliveries (GET /webhook-logs), newest first; empty filters are left off. */
export const getWebhookLogListApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getWebhookLogList: builder.query<WebhookLog[], GetWebhookLogListArgs>({
      queryFn: async (args) => {
        try {
          const params = toParams(args);
          const res = await axios.get<WebhookLogListResponseDto>(
            `${backendBase()}/webhook-logs`,
            { params, headers: { Accept: "application/json" } },
          );
          const webhookLogs = res.data.webhookLogs.map((x) => toWebhookLogFromWebhookLogDto(x));
          return { data: webhookLogs };
        }
        catch (e) {
          if (axios.isAxiosError(e) && e.response) {
            return {
              error: { status: e.response.status, data: e.response.data } as FetchBaseQueryError,
            };
          }
          return {
            error: { status: "FETCH_ERROR", error: String(e) } as FetchBaseQueryError,
          };
        }
      },
      providesTags: [{ type: WebhookLogTags.WebhookLog, id: "LIST" }],
    }),
  }),
});

export const getWebhookLogList = getWebhookLogListApi.endpoints.getWebhookLogList;
